import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Search, Radar, Filter, X, Bookmark, ExternalLink, Loader2, CheckSquare, Square, Trash2, Brain, ChevronDown, Plus, Upload, FileText } from "lucide-react";
import WinProbabilityExplainer from "@/components/WinProbabilityExplainer";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR"];

function OppCard({ o, onSave, saving, selectable, selected, onToggle }) {
  return (
    <div className="p-5 border-b border-white/5 hover:bg-white/[0.02] transition-colors flex items-start gap-4" data-testid={`opp-${o.notice_id}`}>
      {selectable && (
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(o.notice_id); }} className="shrink-0 mt-1" data-testid={`select-${o.notice_id}`}>
          {selected ? <CheckSquare className="w-5 h-5 text-cyan-400" /> : <Square className="w-5 h-5 text-slate-500 hover:text-cyan-400" />}
        </button>
      )}
      <Link to={`/opportunities/${o.notice_id}`} className="min-w-0 flex-1 block">
        <div className="mono-label text-slate-500 mb-2 flex items-center gap-3 flex-wrap">
          <span>{o.agency || "—"}</span>
          {o.state && <span className="text-cyan-400">{o.state}</span>}
          {o.response_deadline && <span>DUE {new Date(o.response_deadline).toLocaleDateString()}</span>}
          {o.contract_value ? <span className="text-green-400">${Number(o.contract_value).toLocaleString()}</span> : null}
        </div>
        <div className="font-display font-semibold text-lg text-white mb-2">{o.title}</div>
        {o.description && !o.description.startsWith("http") && <div className="text-sm text-slate-400 line-clamp-2 mb-2">{o.description.substring(0, 220)}</div>}
        <div className="flex flex-wrap gap-2 mt-3">
          {o.naics && <span className="mono-label text-cyan-400 border border-cyan-400/30 px-2 py-0.5">NAICS {o.naics}</span>}
          {o.set_aside && <span className="mono-label text-amber-400 border border-amber-400/30 px-2 py-0.5 truncate max-w-[280px]">{o.set_aside}</span>}
          {o.pipeline_stage && <span className="mono-label text-slate-400 border border-white/10 px-2 py-0.5 capitalize">{o.pipeline_stage.replace(/_/g, " ")}</span>}
        </div>
      </Link>
      <div className="text-right shrink-0 flex flex-col items-end gap-2">
        <div>
          <div className={`font-display font-black text-4xl ${o.match_score >= 85 ? "text-cyan-400" : o.match_score >= 70 ? "text-amber-400" : "text-slate-500"}`}>{o.match_score}</div>
          <div className="mono-label text-slate-500 text-right">MATCH</div>
          {o.notice_id && (!onSave || o.saved) && (
            <div className="mt-1 flex justify-end" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              <WinProbabilityExplainer noticeId={o.notice_id} aiProbability={o.bid_analysis?.win_probability} />
            </div>
          )}
        </div>
        {onSave && (
          o.saved ? <span className="mono-label text-green-400 flex items-center gap-1" data-testid={`saved-${o.notice_id}`}><Bookmark className="w-3 h-3" fill="currentColor" />SAVED</span>
                  : <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSave(o.notice_id); }} disabled={saving === o.notice_id} className="mono-label text-cyan-400 hover:text-cyan-300 flex items-center gap-1 disabled:opacity-50" data-testid={`save-${o.notice_id}`}>
                      {saving === o.notice_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bookmark className="w-3 h-3" />}SAVE
                    </button>
        )}
        {o.url && <a href={o.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="mono-label text-slate-500 hover:text-cyan-400 flex items-center gap-1"><ExternalLink className="w-3 h-3" />SAM</a>}
      </div>
    </div>
  );
}

export default function Opportunities() {
  const [urlParams, setUrlParams] = useSearchParams();
  // Restore last-used tab from either URL (?tab=search) or sessionStorage.
  // This survives the browser back-button + hard reloads.
  const stored = (() => {
    try { return JSON.parse(sessionStorage.getItem("bf_opps_state") || "null"); } catch { return null; }
  })();
  const initialTab = urlParams.get("tab") === "search"
    ? "search"
    : (stored?.tab === "search" ? "search" : "saved");
  const [tab, setTabState] = useState(initialTab);
  const setTab = (t) => {
    setTabState(t);
    const next = new URLSearchParams(urlParams);
    if (t === "saved") next.delete("tab"); else next.set("tab", t);
    setUrlParams(next, { replace: false });
    try {
      const cur = JSON.parse(sessionStorage.getItem("bf_opps_state") || "{}");
      sessionStorage.setItem("bf_opps_state", JSON.stringify({ ...cur, tab: t }));
    } catch (err) {
      // sessionStorage can throw in private mode / when quota-exceeded — non-critical, keep the tab switch working.
      console.warn("Opps tab persist failed:", err);
    }
  };
  const [opps, setOpps] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkStage, setBulkStage] = useState("interested");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customForm, setCustomForm] = useState({
    title: "",
    agency: "",
    response_deadline: "",
    naics: "",
    place_of_performance: "",
    scope_summary: "",
    trades: "all",
  });
  const [customFiles, setCustomFiles] = useState([]);
  const [customCreating, setCustomCreating] = useState(false);
  const customFileRef = useRef(null);
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ q: "", state: "", agency: "", min_score: "", min_value: "", max_value: "", due_before: "" });
  const [filterValues, setFilterValues] = useState({ agencies: [], states: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [searchParams, setSearchParams] = useState(() => {
    const fromUrl = {
      q: urlParams.get("q") || "",
      naics: urlParams.get("naics") || "",
      state: urlParams.get("state") || "",
      agency: urlParams.get("agency") || "",
      set_aside: urlParams.get("set_aside") || "",
      min_value: urlParams.get("min_value") || "",
      max_value: urlParams.get("max_value") || "",
      min_score: urlParams.get("min_score") || "",
      due_before: urlParams.get("due_before") || "",
      days_back: Number(urlParams.get("days_back")) || 30,
      sort: urlParams.get("sort") || "score",
    };
    const anyUrl = Object.values(fromUrl).some(v => v !== "" && v !== 30 && v !== "score");
    if (anyUrl) return fromUrl;
    return stored?.searchParams || fromUrl;
  });
  const [showSearchAdv, setShowSearchAdv] = useState(false);
  const [restoredOpps, setRestoredOpps] = useState(() => stored?.opps || null);

  const buildQuery = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) p.append(k, v); });
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  const loadSaved = useCallback(async () => {
    try {
      const r = await api.get(`/opportunities${buildQuery()}`);
      setOpps(r.data);
    } catch { /* silent */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (tab === "saved") loadSaved();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filters]);

  useEffect(() => {
    api.get("/opportunities/filters/values").then(r => setFilterValues(r.data)).catch(() => {});
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/opportunities/refresh");
      toast.success(`Loaded ${r.data.new} new · ${r.data.total} total`);
      await loadSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const runSearch = async () => {
    setSearching(true);
    // Persist the search into the URL so the back button restores state.
    const url = new URLSearchParams({ tab: "search" });
    Object.entries(searchParams).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined && v !== 30) url.set(k, String(v));
    });
    setUrlParams(url, { replace: false });
    try {
      const params = new URLSearchParams();
      Object.entries(searchParams).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) params.append(k, v); });
      const r = await api.get(`/opportunities/search?${params.toString()}`);
      setOpps(r.data);
      // Persist to sessionStorage so back-button + reloads restore the exact search state.
      try {
        sessionStorage.setItem("bf_opps_state", JSON.stringify({
          tab: "search",
          searchParams,
          opps: r.data.slice(0, 100),
          at: Date.now(),
        }));
      } catch (err) {
        // Non-critical — search results are still in memory even if we can't cache them.
        console.warn("Opps results cache failed:", err);
      }
      toast.success(`Found ${r.data.length} on SAM.gov`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Search failed"); }
    finally { setSearching(false); }
  };

  // Restore results after a back-button navigation from OpportunityDetail without re-fetching.
  useEffect(() => {
    if (tab === "search" && restoredOpps && restoredOpps.length > 0 && opps.length === 0) {
      setOpps(restoredOpps);
      setRestoredOpps(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // If we land on the page with URL search params (e.g. via deep link), auto-run the search.
  const hasUrlSearch = ["q","naics","state","agency","set_aside","min_value","max_value","min_score","due_before","sort","days_back"].some(k => urlParams.get(k));
  useEffect(() => {
    if (tab === "search" && hasUrlSearch && opps.length === 0 && !restoredOpps) {
      runSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const saveOpp = async (notice_id) => {
    setSaving(notice_id);
    try {
      await api.post("/opportunities/save", { notice_id });
      setOpps(opps.map(o => o.notice_id === notice_id ? { ...o, saved: true } : o));
      toast.success("Saved to pipeline");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(null); }
  };

  const resetFilters = () => setFilters({ q: "", state: "", agency: "", min_score: "", min_value: "", max_value: "", due_before: "" });
  const activeFilterCount = Object.values(filters).filter(v => v !== "" && v !== null && v !== undefined).length;

  const toggleSelect = (id) => setSelected(s => {
    const next = new Set(s); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const selectAll = () => setSelected(new Set(opps.map(o => o.notice_id)));
  const clearSel = () => setSelected(new Set());

  const bulkChangeStage = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post("/opportunities/bulk/stage", { notice_ids: Array.from(selected), stage: bulkStage });
      toast.success(`${r.data.updated} moved to ${bulkStage}`);
      clearSel(); loadSaved();
    } catch { toast.error("Bulk failed"); }
    finally { setBulkBusy(false); }
  };
  const bulkArchive = async () => {
    if (!window.confirm(`Delete ${selected.size} opportunities from your pipeline?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/opportunities/bulk/archive", { notice_ids: Array.from(selected) });
      toast.success(`${r.data.deleted} archived`);
      clearSel(); loadSaved();
    } catch { toast.error("Failed"); }
    finally { setBulkBusy(false); }
  };
  const bulkAnalyze = async () => {
    if (selected.size > 5) { toast.error("Max 5 at once (rate limits)"); return; }
    setBulkBusy(true);
    try {
      const r = await api.post("/opportunities/bulk/analyze", { notice_ids: Array.from(selected) });
      toast.success(`Analyzed ${r.data.processed}`);
      clearSel(); loadSaved();
    } catch { toast.error("Failed"); }
    finally { setBulkBusy(false); }
  };

  const createCustom = async () => {
    if (!customForm.title.trim()) { toast.error("Give the opportunity a title first"); return; }
    setCustomCreating(true);
    try {
      const fd = new FormData();
      Object.entries(customForm).forEach(([k, v]) => v && fd.append(k, v));
      customFiles.forEach((f) => fd.append("files", f));
      const r = await api.post("/opportunities/custom", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Opportunity created" + (r.data.takeoff_id ? " — running AI extraction on your plans" : ""));
      setShowAddCustom(false);
      setCustomForm({ title: "", agency: "", response_deadline: "", naics: "", place_of_performance: "", scope_summary: "", trades: "all" });
      setCustomFiles([]);
      // If plans were attached, kick off the AI generation and jump to the takeoff editor
      if (r.data.takeoff_id) {
        try { await api.post(`/takeoffs/${r.data.takeoff_id}/generate`); } catch { /* takeoff exists but generation didn't queue — user can still hit Generate manually */ }
        navigate(`/takeoffs/${r.data.takeoff_id}`);
      } else {
        navigate(`/opportunities/${r.data.notice_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create opportunity");
    } finally {
      setCustomCreating(false);
    }
  };

  return (
    <div className="p-8 stagger-in" data-testid="opportunities-page">
      {showAddCustom && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto pt-12 pb-12"
          onClick={(e) => { if (e.target === e.currentTarget) setShowAddCustom(false); }}
          data-testid="add-custom-modal"
        >
          <div className="bg-[#0D1424] border border-white/10 rounded-2xl shadow-2xl w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <div className="mono-label text-amber-400">// ADD CUSTOM OPPORTUNITY</div>
                <div className="text-white font-display font-bold text-lg mt-0.5">State / local / private bid</div>
              </div>
              <button onClick={() => setShowAddCustom(false)} className="text-slate-400 hover:text-white p-1" data-testid="add-custom-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="mono-label text-slate-400 block mb-1">TITLE *</label>
                  <input value={customForm.title} onChange={(e) => setCustomForm({ ...customForm, title: e.target.value })} placeholder="Test Elementary — Roof Replacement" className="w-full bg-[#070B14] border border-white/10 focus:border-amber-400 px-3 py-2 text-sm text-white outline-none" data-testid="custom-title" />
                </div>
                <div>
                  <label className="mono-label text-slate-400 block mb-1">AGENCY / OWNER</label>
                  <input value={customForm.agency} onChange={(e) => setCustomForm({ ...customForm, agency: e.target.value })} placeholder="Austin ISD" className="w-full bg-[#070B14] border border-white/10 focus:border-amber-400 px-3 py-2 text-sm text-white outline-none" data-testid="custom-agency" />
                </div>
                <div>
                  <label className="mono-label text-slate-400 block mb-1">RESPONSE DEADLINE</label>
                  <input type="date" value={customForm.response_deadline} onChange={(e) => setCustomForm({ ...customForm, response_deadline: e.target.value })} className="w-full bg-[#070B14] border border-white/10 focus:border-amber-400 px-3 py-2 text-sm text-white outline-none" data-testid="custom-deadline" />
                </div>
                <div>
                  <label className="mono-label text-slate-400 block mb-1">NAICS</label>
                  <input value={customForm.naics} onChange={(e) => setCustomForm({ ...customForm, naics: e.target.value })} placeholder="238160" className="w-full bg-[#070B14] border border-white/10 focus:border-amber-400 px-3 py-2 text-sm text-white outline-none" data-testid="custom-naics" />
                </div>
                <div className="md:col-span-2">
                  <label className="mono-label text-slate-400 block mb-1">PLACE OF PERFORMANCE</label>
                  <input value={customForm.place_of_performance} onChange={(e) => setCustomForm({ ...customForm, place_of_performance: e.target.value })} placeholder="Austin, TX" className="w-full bg-[#070B14] border border-white/10 focus:border-amber-400 px-3 py-2 text-sm text-white outline-none" data-testid="custom-place" />
                </div>
                <div className="md:col-span-2">
                  <label className="mono-label text-slate-400 block mb-1">SCOPE SUMMARY (optional)</label>
                  <textarea value={customForm.scope_summary} onChange={(e) => setCustomForm({ ...customForm, scope_summary: e.target.value })} rows={2} placeholder="Full roof replacement, single-ply TPO membrane…" className="w-full bg-[#070B14] border border-white/10 focus:border-amber-400 px-3 py-2 text-sm text-white outline-none" data-testid="custom-scope" />
                </div>
                <div className="md:col-span-2">
                  <label className="mono-label text-slate-400 block mb-1">TRADES (comma-separated or "all")</label>
                  <input value={customForm.trades} onChange={(e) => setCustomForm({ ...customForm, trades: e.target.value })} placeholder='all' className="w-full bg-[#070B14] border border-white/10 focus:border-amber-400 px-3 py-2 text-sm text-white outline-none" data-testid="custom-trades" />
                </div>
              </div>

              <div className="border border-dashed border-white/20 rounded-xl p-4 bg-black/30">
                <div className="mono-label text-amber-400 mb-2">// PLANS / SPECS / BID DOCS</div>
                <div className="text-xs text-slate-400 mb-3">
                  Attach the drawings, specs, bid tab, or a whole ZIP folder — BidForge auto-generates the quantity takeoff in the background.
                </div>
                <input
                  ref={customFileRef}
                  type="file"
                  className="hidden"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.docx,.xlsx,.xls,.csv,.zip"
                  onChange={(e) => setCustomFiles(Array.from(e.target.files || []))}
                  data-testid="custom-file-input"
                />
                <button
                  onClick={() => customFileRef.current?.click()}
                  className="mono-label border border-white/20 hover:border-amber-400 hover:text-amber-400 px-4 py-2 text-white flex items-center gap-2"
                  data-testid="custom-file-btn"
                >
                  <Upload className="w-3.5 h-3.5" />
                  {customFiles.length ? `${customFiles.length} file${customFiles.length === 1 ? "" : "s"} selected` : "SELECT FILES / ZIP"}
                </button>
                {customFiles.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="custom-file-list">
                    {customFiles.map((f, i) => (
                      <div key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-white/5 text-xs text-slate-300 border border-white/10">
                        <FileText className="w-3 h-3" />
                        <span className="max-w-[200px] truncate">{f.name}</span>
                        <button onClick={() => setCustomFiles(customFiles.filter((_, j) => j !== i))} className="ml-1 hover:text-red-400"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
                <button onClick={() => setShowAddCustom(false)} className="mono-label text-slate-400 hover:text-white px-4 py-2.5" data-testid="custom-cancel">
                  CANCEL
                </button>
                <button
                  onClick={createCustom}
                  disabled={customCreating || !customForm.title.trim()}
                  className="bg-amber-400 text-[#070B14] hover:bg-amber-300 disabled:opacity-40 px-5 py-2.5 mono-label flex items-center gap-2"
                  data-testid="custom-submit"
                >
                  {customCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {customCreating ? "CREATING…" : "CREATE OPPORTUNITY"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="mono-label text-cyan-400 mb-2">// SAM.GOV FEED</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Opportunities</h1>
          <p className="text-slate-400 mt-2">{tab === "saved" ? `${opps.length} in your pipeline` : `${opps.length} results from live SAM.gov search`}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddCustom(true)}
            className="border border-amber-400/50 text-amber-400 hover:bg-amber-400/10 px-4 py-2.5 mono-label flex items-center gap-2"
            data-testid="add-custom-btn"
          >
            <Plus className="w-4 h-4" />ADD CUSTOM
          </button>
          {tab === "saved" && <button onClick={refresh} disabled={refreshing} className="bg-cyan-400 text-[#070B14] px-5 py-2.5 mono-label hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />{refreshing ? "Scanning" : "Refresh"}
          </button>}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10 mb-6">
        <button onClick={() => setTab("saved")} className={`px-5 py-3 mono-label transition-colors ${tab === "saved" ? "text-cyan-400 border-b-2 border-cyan-400 -mb-px" : "text-slate-400 hover:text-white"}`} data-testid="tab-saved">MY PIPELINE</button>
        <button onClick={() => setTab("search")} className={`px-5 py-3 mono-label transition-colors ${tab === "search" ? "text-cyan-400 border-b-2 border-cyan-400 -mb-px" : "text-slate-400 hover:text-white"}`} data-testid="tab-search">SEARCH ALL SAM.GOV</button>
      </div>

      {tab === "saved" && (
        <>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
              <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search title, agency…" className="w-full bg-[#0D1424] border border-white/10 focus:border-cyan-400 pl-10 pr-4 py-2.5 text-white outline-none" data-testid="search-input" />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className="border border-white/10 hover:border-cyan-400 px-4 py-2.5 mono-label flex items-center gap-2" data-testid="toggle-filters">
              <Filter className="w-4 h-4" />FILTERS{activeFilterCount > 0 && <span className="text-cyan-400">· {activeFilterCount}</span>}
            </button>
            {activeFilterCount > 0 && <button onClick={resetFilters} className="mono-label text-slate-400 hover:text-red-400 flex items-center gap-1" data-testid="clear-filters"><X className="w-3.5 h-3.5" />CLEAR</button>}
          </div>

          {showFilters && (
            <div className="bg-[#0D1424] border border-white/10 p-5 mb-6 grid md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="filters-panel">
              <div>
                <div className="mono-label text-slate-400 mb-2">State</div>
                <select value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="filter-state">
                  <option value="">All states</option>
                  {(filterValues.states.length ? filterValues.states : US_STATES).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <div className="mono-label text-slate-400 mb-2">Agency</div>
                <select value={filters.agency} onChange={(e) => setFilters({ ...filters, agency: e.target.value })} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="filter-agency">
                  <option value="">All agencies</option>
                  {filterValues.agencies.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <div className="mono-label text-slate-400 mb-2">Min match score</div>
                <input type="number" min="0" max="100" value={filters.min_score} onChange={(e) => setFilters({ ...filters, min_score: e.target.value })} placeholder="e.g. 70" className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="filter-score" />
              </div>
              <div>
                <div className="mono-label text-slate-400 mb-2">Due before</div>
                <input type="date" value={filters.due_before} onChange={(e) => setFilters({ ...filters, due_before: e.target.value })} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="filter-due" />
              </div>
              <div>
                <div className="mono-label text-slate-400 mb-2">Min contract $</div>
                <input type="number" value={filters.min_value} onChange={(e) => setFilters({ ...filters, min_value: e.target.value })} placeholder="e.g. 100000" className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="filter-min-value" />
              </div>
              <div>
                <div className="mono-label text-slate-400 mb-2">Max contract $</div>
                <input type="number" value={filters.max_value} onChange={(e) => setFilters({ ...filters, max_value: e.target.value })} placeholder="e.g. 5000000" className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="filter-max-value" />
              </div>
            </div>
          )}
        </>
      )}

      {tab === "search" && (
        <div className="bg-[#0D1424] border border-white/10 p-5 mb-6" data-testid="search-panel">
          <div className="grid md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <div className="mono-label text-slate-400 mb-2">Keyword</div>
              <input value={searchParams.q} onChange={(e) => setSearchParams({ ...searchParams, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder="e.g. roofing, HVAC" className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="search-keyword" />
            </div>
            <div>
              <div className="mono-label text-slate-400 mb-2">NAICS</div>
              <input value={searchParams.naics} onChange={(e) => setSearchParams({ ...searchParams, naics: e.target.value })} placeholder="238160" className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="search-naics" />
            </div>
            <div>
              <div className="mono-label text-slate-400 mb-2">State</div>
              <select value={searchParams.state} onChange={(e) => setSearchParams({ ...searchParams, state: e.target.value })} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="search-state">
                <option value="">Any</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <div className="mono-label text-slate-400 mb-2">Posted (days back)</div>
              <select value={searchParams.days_back} onChange={(e) => setSearchParams({ ...searchParams, days_back: Number(e.target.value) })} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="search-days">
                <option value="7">7 days</option><option value="14">14 days</option><option value="30">30 days</option><option value="60">60 days</option><option value="90">90 days</option>
              </select>
            </div>
          </div>

          {showSearchAdv && (
            <div className="grid md:grid-cols-5 gap-4 mt-4 pt-4 border-t border-white/5" data-testid="search-advanced">
              <div>
                <div className="mono-label text-slate-400 mb-2">Agency contains</div>
                <input value={searchParams.agency} onChange={(e) => setSearchParams({ ...searchParams, agency: e.target.value })} placeholder="e.g. USACE, VA" className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="search-agency" />
              </div>
              <div>
                <div className="mono-label text-slate-400 mb-2">Set-aside</div>
                <select value={searchParams.set_aside} onChange={(e) => setSearchParams({ ...searchParams, set_aside: e.target.value })} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="search-set-aside">
                  <option value="">Any</option>
                  <option value="SBA">Small Business</option>
                  <option value="SDVOSB">SDVOSB</option>
                  <option value="8A">8(a)</option>
                  <option value="HUBZone">HUBZone</option>
                  <option value="WOSB">WOSB</option>
                  <option value="EDWOSB">EDWOSB</option>
                  <option value="Total_Small_Business">Total Small Business</option>
                </select>
              </div>
              <div>
                <div className="mono-label text-slate-400 mb-2">Min contract $</div>
                <input type="number" value={searchParams.min_value} onChange={(e) => setSearchParams({ ...searchParams, min_value: e.target.value })} placeholder="100000" className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="search-min-value" />
              </div>
              <div>
                <div className="mono-label text-slate-400 mb-2">Max contract $</div>
                <input type="number" value={searchParams.max_value} onChange={(e) => setSearchParams({ ...searchParams, max_value: e.target.value })} placeholder="5000000" className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="search-max-value" />
              </div>
              <div>
                <div className="mono-label text-slate-400 mb-2">Min match score</div>
                <input type="number" min="0" max="100" value={searchParams.min_score} onChange={(e) => setSearchParams({ ...searchParams, min_score: e.target.value })} placeholder="70" className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="search-min-score" />
              </div>
              <div>
                <div className="mono-label text-slate-400 mb-2">Bid due by</div>
                <input type="date" value={searchParams.due_before} onChange={(e) => setSearchParams({ ...searchParams, due_before: e.target.value })} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="search-due-before" />
              </div>
              <div>
                <div className="mono-label text-slate-400 mb-2">Sort by</div>
                <select value={searchParams.sort} onChange={(e) => setSearchParams({ ...searchParams, sort: e.target.value })} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="search-sort">
                  <option value="score">Best match</option>
                  <option value="deadline">Deadline (soonest)</option>
                  <option value="value">Value (highest)</option>
                  <option value="recent">Recently posted</option>
                </select>
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <button onClick={runSearch} disabled={searching} className="bg-cyan-400 text-[#070B14] px-6 py-2.5 mono-label hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2" data-testid="run-search">
              {searching ? <><Loader2 className="w-4 h-4 animate-spin" />SEARCHING</> : <><Search className="w-4 h-4" />SEARCH SAM.GOV</>}
            </button>
            <button onClick={() => setShowSearchAdv(!showSearchAdv)} className="mono-label border border-white/20 hover:border-cyan-400 hover:text-cyan-400 px-4 py-2.5 flex items-center gap-2" data-testid="toggle-search-advanced">
              <Filter className="w-3.5 h-3.5" />{showSearchAdv ? "HIDE ADVANCED" : "MORE FILTERS"}
            </button>
            <button onClick={() => setSearchParams({ q: "", naics: "", state: "", agency: "", set_aside: "", min_value: "", max_value: "", min_score: "", due_before: "", days_back: 30, sort: "score" })} className="mono-label text-slate-400 hover:text-red-400 flex items-center gap-1" data-testid="reset-search"><X className="w-3.5 h-3.5" />RESET</button>
          </div>
        </div>
      )}

      <div className="bg-[#0D1424] border border-white/10">
        {tab === "saved" && opps.length > 0 && (
          <div className="p-3 border-b border-white/10 flex items-center gap-3 flex-wrap bg-black/20" data-testid="bulk-bar">
            <button onClick={selected.size === opps.length ? clearSel : selectAll} className="mono-label text-slate-400 hover:text-cyan-400 flex items-center gap-2" data-testid="bulk-select-all">
              {selected.size === opps.length ? <CheckSquare className="w-4 h-4 text-cyan-400" /> : <Square className="w-4 h-4" />}
              {selected.size === 0 ? "SELECT ALL" : `${selected.size} SELECTED`}
            </button>
            {selected.size > 0 && (
              <>
                <div className="flex items-center gap-2">
                  <select value={bulkStage} onChange={(e) => setBulkStage(e.target.value)} className="bg-[#070B14] border border-white/10 px-2 py-1.5 text-xs mono-label outline-none" data-testid="bulk-stage-select">
                    {["new","ai_reviewing","interested","estimating","in_progress","needs_review","submitted","awarded","lost","follow_up"].map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                  <button onClick={bulkChangeStage} disabled={bulkBusy} className="mono-label bg-cyan-400 text-[#070B14] px-3 py-1.5 hover:bg-cyan-300 disabled:opacity-50" data-testid="bulk-stage-apply">MOVE</button>
                </div>
                <button onClick={bulkAnalyze} disabled={bulkBusy || selected.size > 5} className="mono-label border border-cyan-400 text-cyan-400 px-3 py-1.5 hover:bg-cyan-400/10 disabled:opacity-40 flex items-center gap-2" data-testid="bulk-analyze"><Brain className="w-3.5 h-3.5" />ANALYZE ({selected.size}/5)</button>
                <button onClick={bulkArchive} disabled={bulkBusy} className="mono-label border border-red-400 text-red-400 px-3 py-1.5 hover:bg-red-400/10 flex items-center gap-2" data-testid="bulk-archive"><Trash2 className="w-3.5 h-3.5" />ARCHIVE</button>
                <button onClick={clearSel} className="mono-label text-slate-400 hover:text-white ml-auto" data-testid="bulk-clear">CLEAR</button>
              </>
            )}
          </div>
        )}
        {opps.length === 0 && (
          <div className="p-12 text-center text-slate-500">
            <Radar className="w-10 h-10 mx-auto mb-4 text-slate-600" />
            {tab === "saved"
              ? <div>No opportunities in pipeline. Click <span className="text-cyan-400">Refresh</span> or use <span className="text-cyan-400">Search All SAM.gov</span>.</div>
              : <div>No results. Try broader criteria.</div>}
          </div>
        )}
        {opps.map((o) => <OppCard key={o.notice_id} o={o} onSave={tab === "search" ? saveOpp : undefined} saving={saving} selectable={tab === "saved"} selected={selected.has(o.notice_id)} onToggle={toggleSelect} />)}
      </div>
    </div>
  );
}
